<script setup>
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesDeliveryReceiptOptionCard from './SalesDeliveryReceiptOptionCard.vue';
import SalesDeliveryReceiptUpload from './SalesDeliveryReceiptUpload.vue';
import manualReceiptImage from 'dashboard/assets/real-estate/sales-delivery/recibo-manual.svg';
import docusignReceiptImage from 'dashboard/assets/real-estate/sales-delivery/recibo-docusign.svg';

defineProps({
  canContinue: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  receiptDocument: { type: Object, default: null },
  receiptType: { type: String, default: 'manual' },
  removing: { type: Boolean, default: false },
  savingType: { type: Boolean, default: false },
  uploading: { type: Boolean, default: false },
});

const emit = defineEmits(['back', 'remove', 'select-type', 'submit', 'upload']);
const { t } = useI18n();
</script>

<template>
  <main
    class="mx-auto flex w-full max-w-[1280px] flex-col gap-5 px-4 pb-32 sm:px-6 lg:px-10"
  >
    <section
      class="rounded-lais border border-lais-border-soft bg-white p-5 shadow-lais-card sm:p-6"
    >
      <h1 class="text-xl font-semibold text-lais-text">
        {{ t('SALES.DELIVERY.START.OPTION.TITLE') }}
      </h1>
      <div
        class="mt-5 grid gap-4 md:grid-cols-2"
        role="radiogroup"
        :aria-label="t('SALES.DELIVERY.START.OPTION.ARIA')"
      >
        <SalesDeliveryReceiptOptionCard
          :badge="t('SALES.DELIVERY.START.OPTION.MANUAL.BADGE')"
          :description="t('SALES.DELIVERY.START.OPTION.MANUAL.DESCRIPTION')"
          :image="manualReceiptImage"
          :image-alt="t('SALES.DELIVERY.START.OPTION.MANUAL.IMAGE_ALT')"
          :label="t('SALES.DELIVERY.START.OPTION.MANUAL.LABEL')"
          :selected="receiptType === 'manual'"
          :disabled="!canEdit || savingType"
          :title="t('SALES.DELIVERY.START.OPTION.MANUAL.TITLE')"
          value="manual"
          @select="emit('select-type', $event)"
        />
        <SalesDeliveryReceiptOptionCard
          :badge="t('SALES.DELIVERY.START.OPTION.DOCUSIGN.BADGE')"
          :description="t('SALES.DELIVERY.START.OPTION.DOCUSIGN.DESCRIPTION')"
          :image="docusignReceiptImage"
          :image-alt="t('SALES.DELIVERY.START.OPTION.DOCUSIGN.IMAGE_ALT')"
          :label="t('SALES.DELIVERY.START.OPTION.DOCUSIGN.LABEL')"
          :selected="receiptType === 'docusign'"
          :disabled="!canEdit || savingType"
          :title="t('SALES.DELIVERY.START.OPTION.DOCUSIGN.TITLE')"
          value="docusign"
          @select="emit('select-type', $event)"
        />
      </div>
    </section>

    <SalesDeliveryReceiptUpload
      :can-remove="canEdit"
      :can-upload="canEdit"
      :disabled="!canEdit"
      :document="receiptDocument"
      :receipt-type="receiptType"
      :removing="removing"
      :uploading="uploading"
      @remove="emit('remove')"
      @upload="emit('upload', $event)"
    />
  </main>

  <footer
    class="fixed inset-x-0 bottom-0 z-20 border-t border-lais-border-soft bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur sm:px-6 lg:px-10"
  >
    <div
      class="mx-auto flex w-full max-w-[1280px] items-center justify-between gap-3"
    >
      <LaisPrimaryButton
        variant="ghost"
        icon="i-lucide-file-text"
        @click="emit('back')"
      >
        {{ t('SALES.DELIVERY.START.ACTIONS.BACK_DRAFT') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        icon="i-lucide-check"
        :disabled="!canContinue"
        @click="emit('submit')"
      >
        {{
          receiptType === 'manual'
            ? t('SALES.DELIVERY.START.ACTIONS.DELIVER')
            : t('SALES.DELIVERY.START.ACTIONS.DOCUSIGN')
        }}
      </LaisPrimaryButton>
    </div>
  </footer>
</template>
